
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, PenTool, Trash2, Wand2, Eye, Download, X } from 'lucide-react';
import { Banner } from '@/types/banner';
import { getAllBanners, downloadBanner, deleteBanner } from '@/utils/banner';
import { useToast } from '@/hooks/use-toast';
import { clearUserSession } from '@/utils/sessionManager';

const MasterManageBannersPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [filteredBanners, setFilteredBanners] = useState<Banner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState('all');

  useEffect(() => {
    loadBanners();
  }, []);

  useEffect(() => {
    filterBanners();
  }, [searchQuery, currentTab, banners]);

  const loadBanners = () => {
    setIsLoading(true);
    try {
      const allBanners = getAllBanners();
      setBanners(allBanners);
      setFilteredBanners(allBanners);
    } catch (error) {
      console.error("Error loading banners:", error);
      toast({
        title: "Error loading banners",
        description: "There was a problem loading your banners.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterBanners = () => {
    let filtered = [...banners];
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(banner => 
        banner.name.toLowerCase().includes(query) || 
        banner.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Filter by tab
    if (currentTab === 'ai') {
      filtered = filtered.filter(banner => banner.type === 'ai');
    } else if (currentTab === 'manual') {
      filtered = filtered.filter(banner => banner.type === 'manual');
    }
    
    setFilteredBanners(filtered);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTabChange = (value: string) => {
    setCurrentTab(value);
  };

  const handleDeleteBanner = (id: string) => {
    try {
      deleteBanner(id);
      loadBanners();
      toast({
        title: "Banner deleted",
        description: "The banner has been deleted successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem deleting the banner.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadBanner = async (banner: Banner) => {
    try {
      await downloadBanner(banner);
      toast({
        title: "Downloaded",
        description: "Banner has been downloaded successfully."
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "There was a problem downloading the banner.",
        variant: "destructive"
      });
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      clearUserSession();
      toast({ title: "Logged out successfully" });
      navigate('/login');
    } catch (error) {
      toast({ 
        title: "Logout failed", 
        description: "There was a problem logging you out.", 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCreatedDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const renderBannerContent = () => {
    if (filteredBanners.length === 0) {
      return (
        <div className="py-12 text-center">
          <div className="mx-auto w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <X className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No banners found</h3>
          <p className="mt-1 text-gray-500">
            {searchQuery ? 'Try adjusting your search' : 'Create your first banner to get started'}
          </p>
          {!searchQuery && (
            <div className="mt-6">
              <Button onClick={() => navigate('/master/marketing/ai-banner')}>
                <Plus className="mr-2 h-4 w-4" />
                Create a Banner
              </Button>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
        {filteredBanners.map((banner) => (
          <Card key={banner.id} className="overflow-hidden flex flex-col">
            <div className="relative bg-gray-100 aspect-[2/1] flex items-center justify-center">
              {banner.imageUrl ? (
                <img 
                  src={banner.imageUrl} 
                  alt={banner.name} 
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                  <p className="text-gray-500 font-medium">{banner.size}</p>
                  <p className="text-gray-400 text-sm mt-1">{banner.text}</p>
                </div>
              )}
              <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {banner.size}
              </div>
            </div>
            
            <CardContent className="flex-grow flex flex-col p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-gray-900 line-clamp-1">{banner.name}</h3>
                  <p className="text-xs text-gray-500">{formatCreatedDate(banner.createdAt)}</p>
                </div>
                <div className={`text-xs px-2 py-1 rounded ${
                  banner.type === 'ai' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {banner.type === 'ai' ? 'AI' : 'Manual'}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-3">
                {banner.tags.map((tag, idx) => (
                  <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              
              <div className="mt-auto pt-4 flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                  onClick={() => handleDownloadBanner(banner)}
                >
                  <Download className="h-3 w-3 mr-2" />
                  Download
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                  onClick={() => handleDeleteBanner(banner.id)}
                >
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <MasterLicenseLayout 
      isLoading={isLoading} 
      onLogout={handleLogout}
      activeTab="manage-banners"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Banner Library</h1>
            <p className="text-gray-600">Manage and organize your marketing banners</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button 
              variant="outline" 
              onClick={() => navigate('/master/marketing/banner-creator')}
            >
              <PenTool className="h-4 w-4 mr-2" />
              Manual Creator
            </Button>
            <Button 
              onClick={() => navigate('/master/marketing/ai-banner')}
            >
              <Wand2 className="h-4 w-4 mr-2" />
              AI Generator
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-5 border-b">
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div className="relative w-full md:w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search banners..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="pl-8"
                />
              </div>
              
              <Tabs defaultValue={currentTab} value={currentTab} onValueChange={handleTabChange} className="w-full md:w-auto">
                <TabsList>
                  <TabsTrigger value="all">All Banners</TabsTrigger>
                  <TabsTrigger value="ai">AI Generated</TabsTrigger>
                  <TabsTrigger value="manual">Manually Created</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  {renderBannerContent()}
                </TabsContent>
                <TabsContent value="ai">
                  {renderBannerContent()}
                </TabsContent>
                <TabsContent value="manual">
                  {renderBannerContent()}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterManageBannersPage;
